import React, { useState } from 'react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, FileSpreadsheet, Loader2, Calendar, Package, Wrench, ClipboardCheck, Gauge, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

function ExportPage() {
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preset, setPreset] = useState('week');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [summary, setSummary] = useState(null);

  function getDateRange(presetValue) {
    var today = new Date();
    var start, end;
    end = today.toISOString().split('T')[0];

    if (presetValue === 'today') {
      start = end;
    } else if (presetValue === 'week') {
      var weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      start = weekAgo.toISOString().split('T')[0];
    } else if (presetValue === 'month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    } else if (presetValue === 'year') {
      start = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
    } else {
      return { start: startDate, end: endDate };
    }
    return { start: start, end: end };
  }

  function handlePresetChange(value) {
    setPreset(value);
    if (value !== 'custom') {
      var range = getDateRange(value);
      setStartDate(range.start);
      setEndDate(range.end);
    }
    setSummary(null);
  }

  function fetchPreview() {
     var range = getDateRange(preset);
     if (!range.start || !range.end) {
       toast.error('Please select a date range');
       return;
     }
     if (range.start > range.end) {
       toast.error('Start date cannot be after end date');
       return;
     }

    setPreviewLoading(true);
    api.get('/export/summary', { params: { start_date: range.start, end_date: range.end } })
      .then(function(res) { setSummary(res.data); })
      .catch(function(err) { 
        toast.error(err.response?.data?.detail || 'Failed to load preview');
        setSummary(null);
      })
      .finally(function() { setPreviewLoading(false); });
  }

  function downloadExport(type) {
    var range = getDateRange(preset);
    if (!range.start || !range.end) {
      toast.error('Please select a date range');
      return;
    }
    if (range.start > range.end) {
      toast.error('Start date cannot be after end date');
      return;
    }

    setLoading(true);
    var endpoint = '/export/' + type;
    
    api.get(endpoint, { 
      params: { start_date: range.start, end_date: range.end },
      responseType: 'blob'
    })
      .then(function(res) {
        var blob = new Blob([res.data], { type: 'text/csv' });
        var url = window.URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = type + '_' + range.start + '_to_' + range.end + '.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Export downloaded successfully');
      })
      .catch(function(err) {
        toast.error(err.response?.data?.detail || 'Export failed');
      })
      .finally(function() { setLoading(false); });
  }

  var exportTypes = [
    { id: 'production-runs', name: 'Production Runs', icon: Package, desc: 'All production data with quantities, times, status', enabled: true },
    { id: 'tool-consumption', name: 'Tool Consumption', icon: Wrench, desc: 'Tool/insert usage per production run (Coming Soon)', enabled: false },
    { id: 'inspection-data', name: 'Inspection Data', icon: ClipboardCheck, desc: 'Quality measurements and results (Coming Soon)', enabled: false },
    { id: 'oee-summary', name: 'OEE Summary', icon: Gauge, desc: 'Machine-wise OEE breakdown', enabled: true }
  ];

  return React.createElement('div', { className: 'space-y-6 animate-fade-in max-w-4xl', 'data-testid': 'export-page' },
    React.createElement('div', null,
      React.createElement('h1', { className: 'font-heading text-3xl font-bold text-slate-900' }, 'Export Data'),
      React.createElement('p', { className: 'text-slate-500' }, 'Download production data for payroll, compliance, and reporting')
    ),

    React.createElement(Card, { className: 'border-slate-200' },
      React.createElement(CardHeader, null,
        React.createElement(CardTitle, { className: 'flex items-center gap-2' },
          React.createElement(Calendar, { className: 'w-5 h-5' }), 'Select Date Range'
        ),
        React.createElement(CardDescription, null, 'Choose a preset or custom date range')
      ),
      React.createElement(CardContent, null,
        React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-4 gap-4' },
          React.createElement('div', { className: 'space-y-2' },
            React.createElement(Label, null, 'Preset'),
            React.createElement(Select, { value: preset, onValueChange: handlePresetChange },
              React.createElement(SelectTrigger, { className: 'h-11', 'data-testid': 'preset-select' },
                React.createElement(SelectValue, null)
              ),
              React.createElement(SelectContent, null,
                React.createElement(SelectItem, { value: 'today' }, 'Today'),
                React.createElement(SelectItem, { value: 'week' }, 'Last 7 Days'),
                React.createElement(SelectItem, { value: 'month' }, 'This Month'),
                React.createElement(SelectItem, { value: 'year' }, 'This Year'),
                React.createElement(SelectItem, { value: 'custom' }, 'Custom Range')
              )
            )
          ),
          React.createElement('div', { className: 'space-y-2' },
            React.createElement(Label, null, 'Start Date'),
              React.createElement(Input, {
                type: 'date',
                value: startDate,
                onChange: function(e) { 
                  var newStart = e.target.value;
                  if (endDate && newStart > endDate) {
                    toast.error('Start date cannot be after end date');
                    return;
                  }
                  setStartDate(newStart);
                  setPreset('custom');
                  setSummary(null);
                },
                className: 'h-11 w-40',
                'data-testid': 'start-date'
            })
          ),
          React.createElement('div', { className: 'space-y-2' },
            React.createElement(Label, null, 'End Date'),
              React.createElement(Input, {
                type: 'date',
                value: endDate,
                onChange: function(e) { 
                  var newEnd = e.target.value;
                  if (startDate && newEnd < startDate) {
                    toast.error('End date cannot be before start date');
                    return;
                  }
                  setEndDate(newEnd);
                  setPreset('custom');
                  setSummary(null);
                },
                className: 'h-11 w-40',
                'data-testid': 'end-date'
            })
          ),
          React.createElement('div', { className: 'space-y-2' },
            React.createElement(Label, null, '\u00A0'),
            React.createElement(Button, {
              onClick: fetchPreview,
              disabled: previewLoading,
              className: 'w-full h-11',
              variant: 'outline',
              'data-testid': 'preview-btn'
            },
              previewLoading ? React.createElement(Loader2, { className: 'w-4 h-4 mr-2 animate-spin' }) : null,
              'Preview Data'
            )
          )
        )
      )
    ),

    summary && React.createElement(Card, { className: 'border-slate-200 bg-green-50 border-green-200' },
      React.createElement(CardContent, { className: 'py-4' },
        React.createElement('div', { className: 'flex items-start gap-3' },
          React.createElement(CheckCircle2, { className: 'w-5 h-5 text-green-600 mt-0.5 flex-shrink-0' }),
          React.createElement('div', { className: 'flex-1' },
            React.createElement('p', { className: 'font-medium text-green-800' },
              'Data available for ', summary.date_range.start, ' to ', summary.date_range.end
            ),
            React.createElement('div', { className: 'grid grid-cols-2 md:grid-cols-3 gap-4 mt-3' },
              React.createElement('div', null,
                React.createElement('p', { className: 'text-xs text-green-600 uppercase' }, 'Production Runs'),
                React.createElement('p', { className: 'font-mono text-xl font-bold text-green-900' }, summary.production_runs)
              ),
              React.createElement('div', null,
                React.createElement('p', { className: 'text-xs text-green-600 uppercase' }, 'Total Produced'),
                React.createElement('p', { className: 'font-mono text-xl font-bold text-green-900' }, summary.total_produced.toLocaleString())
              ),
              React.createElement('div', null,
                React.createElement('p', { className: 'text-xs text-green-600 uppercase' }, 'Machines'),
                React.createElement('p', { className: 'font-mono text-xl font-bold text-green-900' }, summary.machines_included)
              )
            )
          )
        )
      )
    ),

    !summary && !previewLoading && React.createElement(Card, { className: 'border-slate-200 bg-blue-50 border-blue-200' },
      React.createElement(CardContent, { className: 'py-4' },
        React.createElement('div', { className: 'flex items-start gap-3' },
          React.createElement(AlertCircle, { className: 'w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0' }),
          React.createElement('div', null,
            React.createElement('p', { className: 'font-medium text-blue-800' }, 'Select a date range and click "Preview Data"'),
            React.createElement('p', { className: 'text-sm text-blue-700 mt-1' }, 'Preview will show available records before downloading')
          )
        )
      )
    ),

    React.createElement(Card, { className: 'border-slate-200' },
      React.createElement(CardHeader, null,
        React.createElement(CardTitle, { className: 'flex items-center gap-2' },
          React.createElement(FileSpreadsheet, { className: 'w-5 h-5' }), 'Export Options'
        ),
        React.createElement(CardDescription, null, 'Download data as CSV files (compatible with Excel, Google Sheets)')
      ),
      React.createElement(CardContent, null,
        React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-4' },
          exportTypes.map(function(exp) {
            var isDisabled = !exp.enabled;
            return React.createElement('div', {
              key: exp.id,
              className: 'p-4 border rounded-lg transition-colors ' + (isDisabled ? 'opacity-50 bg-slate-50' : 'hover:border-primary hover:bg-slate-50')
            },
              React.createElement('div', { className: 'flex items-start justify-between' },
                React.createElement('div', { className: 'flex items-start gap-3' },
                  React.createElement('div', { className: 'p-2 rounded-lg ' + (isDisabled ? 'bg-slate-200' : 'bg-primary/10') },
                    React.createElement(exp.icon, { className: 'w-5 h-5 ' + (isDisabled ? 'text-slate-400' : 'text-primary') })
                  ),
                  React.createElement('div', null,
                    React.createElement('p', { className: 'font-medium ' + (isDisabled ? 'text-slate-400' : 'text-slate-900') }, exp.name),
                    React.createElement('p', { className: 'text-sm mt-0.5 ' + (isDisabled ? 'text-slate-400' : 'text-slate-500') }, exp.desc)
                  )
                ),
                React.createElement(Button, {
                  size: 'sm',
                  disabled: !summary || loading || isDisabled,
                  onClick: function() { downloadExport(exp.id); },
                  'data-testid': 'export-' + exp.id + '-btn'
                },
                  React.createElement(Download, { className: 'w-4 h-4 mr-2' }),
                  isDisabled ? 'Disabled' : 'Download'
                )
              )
            );
          })
        )
      )
    ),

    React.createElement('div', { className: 'bg-slate-100 rounded-lg p-4' },
      React.createElement('h3', { className: 'font-medium text-slate-700 mb-2' }, 'Export File Format'),
      React.createElement('p', { className: 'text-sm text-slate-600' },
        'All exports are in CSV format, which can be opened directly in Microsoft Excel, Google Sheets, or any spreadsheet application. ',
        'Data includes all relevant fields with proper headers for easy filtering and analysis.'
      )
    )
  );
}

export default ExportPage;
